import { Box, Button, Typography } from "@mui/joy";
import Add from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import React, { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import GenericTableView from "../../components/GenericTableView";
import { FilterMatchMode } from "primereact/api";
import { GenericTableColumTypes, GenericTableFilterTypes } from "../../enum/TableView";
import { MenuItem } from "primereact/menuitem";
import { GenericTableColumns } from "../../models/GenericTableViewModel";
import { removeClient, updateClient } from "../../service/clientService";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { RouteComponentProps } from "../../models/RouteComponent";
import { UserTypes } from "../../enum/UserTypes";
import { useNavigate } from "react-router-dom";
import { removeEmployee, updateEmployee } from "../../service/employeeService";
import { siteListRemove, siteListUpdate } from "../../service/siteService";
import SiteSetup from "./Site-Setup";
import AddSite from "./Create/AddSite";
import DuplicateSite from "./Duplicate/DuplicateSite";
import { saveActiveSite, saveActiveType } from "../../service/session-service";

export default function SiteTable(props: RouteComponentProps) {
  const navigate = useNavigate();

  const [selectedRecord, setSelectedRecord] = useState({} as any);
  const [setupVisible, setSetupVisible] = useState(false);
  const [reload, setReload] = useState(false);
  const [modalToview, setModalToView] = useState<
    "add-site" | "incomplete-setup-warning" | "duplicate-site" | null
  >(null);

  const toast = useRef(null as any);

  const clientId = props.session.client_id;

  let url = "site-list";

  const baseFilter = {
    name: { value: "", matchMode: FilterMatchMode.CONTAINS },
    address: { value: "", matchMode: FilterMatchMode.CONTAINS },
    active: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };

  const deleteButton = (row: any) => {
    console.log("deleteButton row == ", selectedRecord);
    removeClient(selectedRecord.id);
  };

  const itemSelected = (row: any) => {
    const value = row.value;
    setSelectedRecord(value);

    if (!value) return;
    viewSelected(value);
    if (value?.setup_complete) {
    } else {
      setSetupVisible(true);
    }
  };

  const contextItemSelected = (row: any) => {
    console.log("contextItemSelected row == ", row);
    setSelectedRecord(row.value);
  };

  const acceptFunc = async () => {
    props.setGlobalLoader(true);
    const client = await siteListRemove(selectedRecord.id);
    props.setGlobalLoader(false);
    setReload(!reload);
    toast.current.show({
      severity: "success",
      summary: "Site Deleted Successfully",
      life: 3000,
    });
  };

  const deleteConfirm = (row: any) => {
    confirmDialog({
      message: "Are you sure you want to proceed?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => acceptFunc(),
    });
  };

  const changeStatusFunc = async (status: boolean) => {
    props.setGlobalLoader(true);
    const client = await siteListUpdate({ active: status }, selectedRecord.id);
    props.setGlobalLoader(false);
    setReload(!reload);
    toast.current.show({
      severity: "success",
      summary: status ? "Site Activated Successfully" : "Site Deactivated Successfully",
      life: 3000,
    });
  };

  const disableConfirm = (row: any) => {
    confirmDialog({
      message: "Are you sure you want to deactivate site?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => changeStatusFunc(false),
    });
  };

  const activateConfirm = (row: any) => {
    confirmDialog({
      message: "Are you sure you want to activate site?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => changeStatusFunc(true),
    });
  };

  const DialogSetVisible = (row: any) => {
    viewSelected(row, "/site-details/details");
  };

  const viewSelected = (row: any, url?: string) => {
    const navigateTo = url ?? "/dashboard";
    const value = row?.id ? row : selectedRecord;
    let breadcrumbs = props.breadcrumbs;
    
    // Save active site to localStorage
    saveActiveSite(value?.id, value?.name);
    saveActiveType(UserTypes.Site);
    
    props.setSession({
      ...props.session,
      ...{
        site_id: value?.id,
        active_type: UserTypes.Site,
        site_name: value?.name,
      },
    });
    breadcrumbs.push(
      {
        label: value.name,
        link: "/site-table",
        userType: UserTypes.Client,
        isLabel: true,
      },
      {
        label: "Dashboard",
        link: "/dashboard",
        userType: UserTypes.Site,
      }
    );
    props.setBreadcrumbs(breadcrumbs);
    navigate(navigateTo);
  };

  const onSiteCreated = (row: any) => {
    const value = row?.id ? row : selectedRecord;
    let breadcrumbs = props.breadcrumbs;
    
    // Save active site to localStorage
    saveActiveSite(value?.id, value?.name);
    saveActiveType(UserTypes.Site);
    
    props.setSession({
      ...props.session,
      ...{
        site_id: value?.id,
        active_type: UserTypes.Site,
        site_name: value?.name,
      },
    });
    breadcrumbs.push(
      {
        label: value.name,
        link: "/site-table",
        userType: UserTypes.Client,
        isLabel: true,
      },
      {
        label: "Appointments",
        link: "/people-and-attendance/appointments",
        userType: UserTypes.Site,
      }
    );
    props.setBreadcrumbs(breadcrumbs);
    navigate("/people-and-attendance/appointments");
  };

  const contextItems = () => {
    let objArray = [
      { label: "View", icon: "pi pi-fw pi-eye", command: viewSelected },
      { label: "Edit", icon: "pi pi-fw pi-pencil", command: DialogSetVisible },
      {
        label: "Duplicate",
        icon: "pi pi-fw pi-copy",
        command: () => {
          setModalToView("duplicate-site");
        },
      },
    ];
    if (selectedRecord?.active) {
      objArray.push({
        label: "Deactivate",
        icon: "pi pi-fw pi-times-circle",
        command: disableConfirm,
      });
    } else {
      objArray.push({
        label: "Activate",
        icon: "pi pi-fw pi-check-circle",
        command: activateConfirm,
      });
    }
    objArray.push({
      label: "Delete",
      icon: "pi pi-fw pi-trash",
      command: deleteConfirm,
    });
    return objArray;
  };

  const columns: GenericTableColumns[] = [
    {
      field: "name",
      header: "Name",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: true,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "industry_list",
      header: "Industry",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "address",
      header: "Address",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "setup_complete",
      header: "Setup Complete",
      type: GenericTableColumTypes.Boolean,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Boolean,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "active",
      header: "Active",
      type: GenericTableColumTypes.Boolean,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Boolean,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "created_at",
      header: "Created At",
      type: GenericTableColumTypes.Date,
      visible: true,
      sort: true,
      filter: false,
    },
  ];

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Your Digital Safety Files
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2.5,
          backgroundColor: "#e3f2fd",
          borderRadius: "md",
          border: "2px solid #2196f3",
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          boxShadow: "0 2px 8px rgba(33, 150, 243, 0.15)",
        }}
      >
        <InfoIcon 
          sx={{ 
            color: "#1976d2", 
            fontSize: "1.25rem",
            mt: 0.25,
            flexShrink: 0
          }} 
        />
        <Box>
          <Typography 
            level="body-md" 
            sx={{ 
              mb: 0.5, 
              fontWeight: "lg", 
              color: "#1565c0",
              lineHeight: 1.4
            }}
          >
            Create and manage your site-specific digital safety files (DSF).
          </Typography>
          <Typography 
            level="body-sm" 
            sx={{ 
              color: "#1976d2",
              lineHeight: 1.5
            }}
          >
            Once a site DSF is 100% compliant, share the link with the Safety Officer for approval and display the QR Code poster on site for instant access.
          </Typography>
        </Box>
      </Box>

      <GenericTableView
        reload={reload}
        baseFilters={baseFilter}
        url={url}
        columns={columns}
        contextItems={contextItems}
        itemSelect={itemSelected}
        contextItemSelected={contextItemSelected}
        addButton={() => {
          setModalToView("add-site");
        }}
      />

      <Dialog
        header="Complete Setup"
        visible={setupVisible}
        style={{ width: "95vw" }}
        onHide={() => setSetupVisible(false)}
      >
        <SiteSetup
          setGlobalLoader={props.setGlobalLoader}
          record={selectedRecord}
          route={props}
          setReload={setReload}
          reload={reload}
          setVisible={setSetupVisible}
          toast={toast}
          session={props.session}
          client={clientId}
        />
      </Dialog>

      <Dialog
        header={"Add Site"}
        visible={modalToview === "add-site"}
        style={{ width: "95vw", height: "95vh", maxWidth: "1440px" }}
        onHide={() => setModalToView(null)}
        closeOnEscape={false}
        closable={false}
      >
        <AddSite
          {...props}
          onCancel={() => setModalToView(null)}
          callback={item => onSiteCreated(item)}
        />
      </Dialog>

      <Dialog
        header={"Duplicate Site"}
        visible={modalToview === "duplicate-site"}
        style={{ width: "95vw", height: "95vh", maxWidth: "1440px" }}
        onHide={() => setModalToView(null)}
        closeOnEscape={false}
        closable={false}
      >
        <DuplicateSite
          {...props}
          onCancel={() => setModalToView(null)}
          callback={item => onSiteCreated(item)}
          record={selectedRecord}
        />
      </Dialog>

      <ConfirmDialog />

      <Toast ref={toast} style={{ zIndex: 9999999 }} />
    </React.Fragment>
  );
}
